// bluaIQ — A.3 polish: TemplateLibraryModal
//
// Full template-library CRUD inside a modal that lives in the project's
// SelectionBoardModule. Replaces the old top-level /selection-templates
// route — the constructor now manages templates from the same screen
// where they apply them, eliminating the cross-module mental jump.
//
// Auto-seed (3 DFW presets) runs the first time this modal opens — not on
// route entry — so users who never click "Manage templates" don't get
// surprise rows on the server. The localStorage flag is identical to the
// previous implementation (bluaiq-templates-seeded-v2) so users who were
// already seeded by the old route remain idempotent.

import { apiFetch } from '../utils/api'
import { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useTranslation } from 'react-i18next'
import {
  Sparkles, Plus, Edit3, Trash2, ArrowUp, ArrowDown, X, Save, HelpCircle,
} from 'lucide-react'

// v2: seed allowances refreshed against DFW 2026 market data + Appliances
// added as 8th category. Bumping the flag from v1 forces a safe re-seed for
// users who still have the original presets untouched. See seeding effect.
const SEED_FLAG_KEY = 'bluaiq-templates-seeded-v2'
const LEGACY_SEED_FLAG_KEY = 'bluaiq-templates-seeded-v1'

// Segment metadata for the 3 preset templates. Not stored in Prisma — we
// derive it from the template name. User-created templates have no entry
// here, which is what gates the "preset-only" badges in the card UI.
type TemplateSegment = 'starter' | 'mid' | 'premium'
const PRESET_SEGMENTS: Record<string, TemplateSegment> = {
  'Modern Minimalist':   'starter',
  'Moderno Minimalista': 'starter',
  'Texas Traditional':   'mid',
  'Tradicional Texano':  'mid',
  'Contemporary Luxe':   'premium',
  'Contemporáneo de Lujo': 'premium',
}
const PRESET_NAMES_ALL = Object.keys(PRESET_SEGMENTS)

const SEGMENT_BADGE_STYLE: Record<TemplateSegment, string> = {
  starter: 'bg-blue-100 text-blue-700',
  mid:     'bg-amber-100 text-amber-700',
  premium: 'bg-purple-100 text-purple-700',
}

// Once dismissed, the educational banner stays hidden in this browser.
const BANNER_DISMISSED_KEY = 'bluaiq-templates-banner-dismissed-v1'

// ── TYPES ─────────────────────────────────────────

interface TemplateCategory {
  id: string
  name: string
  description?: string | null
  allowance: number
  sortOrder: number
}

interface SelectionTemplate {
  id: string
  name: string
  description?: string | null
  categories: TemplateCategory[]
}

interface CategoryDraft {
  name: string
  description: string
  allowance: number
  sortOrder: number
}

interface TemplateDraft {
  name: string
  description: string
  categories: CategoryDraft[]
}

const EMPTY_DRAFT: TemplateDraft = {
  name: '',
  description: '',
  categories: [{ name: '', description: '', allowance: 0, sortOrder: 0 }],
}

// ── DFW SEED PRESETS ──────────────────────────────
// Allowances reflect DFW 2026 market research (NAHB + public sources):
// Modern $260/sqft × 2,200sqft starter, Traditional $280/sqft × 2,600sqft
// mid, Luxe $400/sqft × 3,400sqft premium, finishes ≈ 27-28% of total.

interface SeedPreset {
  name: string
  nameEs: string
  description: string
  descriptionEs: string
  categories: { name: string; nameEs: string; allowance: number }[]
}

const SEED_PRESETS: SeedPreset[] = [
  {
    name: 'Modern Minimalist',
    nameEs: 'Moderno Minimalista',
    description: 'Clean lines, neutral palette, matte black fixtures. Starter style for new builds in Frisco and Plano. Total ~$34,500 in allowances. Based on a 2,200 sqft home at $260/sqft.',
    descriptionEs: 'Líneas limpias, paleta neutra, herrajes negros mate. Estilo inicial para construcciones nuevas en Frisco y Plano. Total ~$34,500 en presupuestos base. Basado en casa promedio de 2,200 sqft a $260/sqft.',
    categories: [
      { name: 'Flooring',   nameEs: 'Pisos',       allowance: 5800 },
      { name: 'Countertop', nameEs: 'Encimeras',   allowance: 3800 },
      { name: 'Tile',       nameEs: 'Azulejos',    allowance: 3500 },
      { name: 'Fixtures',   nameEs: 'Accesorios',  allowance: 2800 },
      { name: 'Cabinets',   nameEs: 'Gabinetes',   allowance: 8500 },
      { name: 'Lighting',   nameEs: 'Iluminación', allowance: 2200 },
      { name: 'Paint',      nameEs: 'Pintura',     allowance: 1400 },
      { name: 'Appliances', nameEs: 'Electrodomésticos', allowance: 6500 },
    ],
  },
  {
    name: 'Texas Traditional',
    nameEs: 'Tradicional Texano',
    description: 'Warm tones, granite, oil-rubbed bronze. Classic DFW suburban style for Allen and McKinney. Total ~$46,400 in allowances. Based on a 2,600 sqft home at $280/sqft.',
    descriptionEs: 'Tonos cálidos, granito, bronce envejecido. Estilo clásico suburbano DFW para Allen y McKinney. Total ~$46,400 en presupuestos base. Basado en casa promedio de 2,600 sqft a $280/sqft.',
    categories: [
      { name: 'Flooring',   nameEs: 'Pisos',       allowance: 7200 },
      { name: 'Countertop', nameEs: 'Encimeras',   allowance: 5500 },
      { name: 'Tile',       nameEs: 'Azulejos',    allowance: 4800 },
      { name: 'Fixtures',   nameEs: 'Accesorios',  allowance: 3800 },
      { name: 'Cabinets',   nameEs: 'Gabinetes',   allowance: 11000 },
      { name: 'Lighting',   nameEs: 'Iluminación', allowance: 2800 },
      { name: 'Paint',      nameEs: 'Pintura',     allowance: 1800 },
      { name: 'Appliances', nameEs: 'Electrodomésticos', allowance: 9500 },
    ],
  },
  {
    name: 'Contemporary Luxe',
    nameEs: 'Contemporáneo de Lujo',
    description: 'Bold textures, mixed metals, statement pieces. Popular in Southlake and Highland Park remodels. Total ~$83,000 in allowances. Based on a 3,400 sqft home at $400/sqft.',
    descriptionEs: 'Texturas audaces, metales mezclados, piezas de impacto. Popular en remodelaciones de Southlake y Highland Park. Total ~$83,000 en presupuestos base. Basado en casa promedio de 3,400 sqft a $400/sqft.',
    categories: [
      { name: 'Flooring',   nameEs: 'Pisos',       allowance: 13500 },
      { name: 'Countertop', nameEs: 'Encimeras',   allowance: 11000 },
      { name: 'Tile',       nameEs: 'Azulejos',    allowance: 8500 },
      { name: 'Fixtures',   nameEs: 'Accesorios',  allowance: 6800 },
      { name: 'Cabinets',   nameEs: 'Gabinetes',   allowance: 18000 },
      { name: 'Lighting',   nameEs: 'Iluminación', allowance: 4500 },
      { name: 'Paint',      nameEs: 'Pintura',     allowance: 2700 },
      { name: 'Appliances', nameEs: 'Electrodomésticos', allowance: 18000 },
    ],
  },
]

// ── DISPLAY-ONLY bilingual mapping for PRELOADED templates ───────────────────
// Templates are persisted DB records, seeded once per browser from SEED_PRESETS
// in whatever language was active at seed time — so an English-seeded preset
// shows English even in Spanish mode (and vice-versa). These helpers translate
// KNOWN preset values to the active language AT RENDER TIME only; they never
// mutate data, and any value that isn't a recognized preset (i.e. a
// user-created template/category) is returned unchanged — never auto-translated.
// Matching is keyed on the template/category name (en OR es), so it works
// regardless of which language the record was seeded in.
export function localizeTemplateName(name: string, isEs: boolean): string {
  const p = SEED_PRESETS.find(s => s.name === name || s.nameEs === name)
  return p ? (isEs ? p.nameEs : p.name) : name
}
export function localizeTemplateDescription(name: string, description: string | null | undefined, isEs: boolean): string {
  const p = SEED_PRESETS.find(s => s.name === name || s.nameEs === name)
  if (p) return isEs ? p.descriptionEs : p.description
  return description || ''
}
export function localizeCategoryName(name: string, isEs: boolean): string {
  for (const p of SEED_PRESETS) {
    const c = p.categories.find(cat => cat.name === name || cat.nameEs === name)
    if (c) return isEs ? c.nameEs : c.name
  }
  return name
}

// ── PROPS ────────────────────────────────────────

interface TemplateLibraryModalProps {
  isOpen: boolean
  onClose: () => void
}

// ── COMPONENT ────────────────────────────────────

export function TemplateLibraryModal({ isOpen, onClose }: TemplateLibraryModalProps) {
  if (!isOpen) return null
  return <TemplateLibraryModalBody onClose={onClose} />
}

// Body lives in its own component so hooks (useQuery, useEffect, etc.) only
// run while the modal is open. Mounting+unmounting on isOpen toggles is what
// makes auto-seed fire on first open instead of on Board mount.
function TemplateLibraryModalBody({ onClose }: { onClose: () => void }) {
  const { getToken } = useAuth()
  const { t, i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  const queryClient = useQueryClient()

  const [editorState, setEditorState] = useState<{ id: string | null; draft: TemplateDraft } | null>(null)
  // Honest save/delete feedback — create/update/delete must never fail silently.
  const [notice, setNotice] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null)
  const showNotice = (kind: 'ok' | 'err', msg: string) => {
    setNotice({ kind, msg })
    setTimeout(() => setNotice(null), 3500)
  }
  const [bannerDismissed, setBannerDismissed] = useState<boolean>(() =>
    typeof window !== 'undefined' && localStorage.getItem(BANNER_DISMISSED_KEY) === '1'
  )

  const dismissBanner = () => {
    setBannerDismissed(true)
    if (typeof window !== 'undefined') localStorage.setItem(BANNER_DISMISSED_KEY, '1')
  }

  // ── Queries ──────────────────────────────────────
  const { data: templates, isLoading } = useQuery<SelectionTemplate[]>({
    queryKey: ['selection-templates'],
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch('/api/selection-templates', {
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      })
      if (!res.ok) throw new Error('API unavailable')
      return res.json()
    },
    retry: false,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['selection-templates'] })

  // ── Mutations ────────────────────────────────────
  const createTpl = useMutation({
    mutationFn: async (draft: TemplateDraft) => {
      const token = await getToken()
      const res = await apiFetch('/api/selection-templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify(serializeDraft(draft)),
      })
      if (!res.ok) throw new Error('CREATE_FAILED')
      return res.json()
    },
    onSuccess: () => { invalidate(); setEditorState(null); showNotice('ok', isEs ? 'Plantilla guardada.' : 'Template saved.') },
    onError: () => showNotice('err', isEs ? 'No se pudo guardar la plantilla. Inténtalo de nuevo.' : 'Could not save the template. Please try again.'),
  })

  const updateTpl = useMutation({
    mutationFn: async ({ id, draft }: { id: string; draft: TemplateDraft }) => {
      const token = await getToken()
      const res = await apiFetch(`/api/selection-templates/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify(serializeDraft(draft)),
      })
      if (!res.ok) throw new Error('UPDATE_FAILED')
      return res.json()
    },
    onSuccess: () => { invalidate(); setEditorState(null); showNotice('ok', isEs ? 'Plantilla guardada.' : 'Template saved.') },
    onError: () => showNotice('err', isEs ? 'No se pudo guardar la plantilla. Inténtalo de nuevo.' : 'Could not save the template. Please try again.'),
  })

  const deleteTpl = useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken()
      const res = await apiFetch(`/api/selection-templates/${id}`, {
        method: 'DELETE',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      })
      if (!res.ok) throw new Error('DELETE_FAILED')
      return res.json()
    },
    onSuccess: () => { invalidate(); showNotice('ok', isEs ? 'Plantilla eliminada.' : 'Template deleted.') },
    onError: () => showNotice('err', isEs ? 'No se pudo eliminar la plantilla. Inténtalo de nuevo.' : 'Could not delete the template. Please try again.'),
  })

  // ── Seed / re-seed logic (v2) ────────────────────
  // Three scenarios, all gated by the v2 flag so we run at most once:
  //   1. New user (zero templates): create the 3 fresh presets.
  //   2. User who got v1 presets and never touched them (exactly the 3
  //      original names, nothing else): delete them, then seed v2.
  //   3. User who has custom templates OR deleted any preset: leave
  //      everything alone — never overwrite their work.
  // We always set the v2 flag at the end so this runs at most once per
  // browser, even when scenario 3 left the DB unchanged.
  useEffect(() => {
    if (isLoading || !templates) return
    if (typeof window === 'undefined') return
    if (localStorage.getItem(SEED_FLAG_KEY)) return

    // Mark as seeded BEFORE firing requests so a slow network or a re-run
    // of the effect can't double-seed.
    localStorage.setItem(SEED_FLAG_KEY, '1')
    // Clean up the old key so DevTools stays tidy on repeat visits.
    localStorage.removeItem(LEGACY_SEED_FLAG_KEY)

    const createPreset = async (preset: SeedPreset) => {
      const token = await getToken()
      const draft: TemplateDraft = {
        name: isEs ? preset.nameEs : preset.name,
        description: isEs ? preset.descriptionEs : preset.description,
        categories: preset.categories.map((c, idx) => ({
          name: isEs ? c.nameEs : c.name,
          description: '',
          allowance: c.allowance,
          sortOrder: idx,
        })),
      }
      await apiFetch('/api/selection-templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify(serializeDraft(draft)),
      })
    }

    const deleteTemplate = async (id: string) => {
      const token = await getToken()
      await apiFetch(`/api/selection-templates/${id}`, {
        method: 'DELETE',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      })
    }

    // Scenario 1: empty list → seed.
    if (templates.length === 0) {
      Promise.all(SEED_PRESETS.map(createPreset)).then(() => invalidate())
      return
    }

    // Scenario 2: list has exactly 3 entries that all match preset names.
    // We accept either ES or EN names so a language toggle between v1 seed
    // and now doesn't trip the heuristic. Anything else → scenario 3.
    const allArePresetNames = templates.every(tpl => PRESET_NAMES_ALL.includes(tpl.name))
    const isCleanV1 = templates.length === 3 && allArePresetNames
    if (isCleanV1) {
      Promise.all(templates.map(tpl => deleteTemplate(tpl.id)))
        .then(() => Promise.all(SEED_PRESETS.map(createPreset)))
        .then(() => invalidate())
    }
    // Scenario 3: noop — flag is now set, we won't ask again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, templates?.length])

  // ── Render ───────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50">
      <div className="relative bg-white w-full sm:max-w-3xl sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Sticky header with title + close */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#ECEAE4] sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-[#06539B]" />
            <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17]">
              {t('selections.templates.title')}
            </h2>
            <span className="text-[11px] font-[600] text-[#9E9890] ml-2">
              {templates?.length ?? 0}
            </span>
          </div>
          <button onClick={onClose} aria-label={t('common.close')} className="text-[#9E9890] hover:text-[#1C1A17]">
            <X size={20} />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Honest save/delete feedback (never silent). */}
          {notice && (
            <div
              data-testid="template-library-notice"
              className={`mb-4 rounded-xl border px-4 py-2.5 text-[12px] font-[700] ${
                notice.kind === 'ok'
                  ? 'border-green-200 bg-green-50 text-green-700'
                  : 'border-red-200 bg-red-50 text-red-700'
              }`}
            >
              {notice.msg}
            </div>
          )}
          {/* Educational banner — expanded by default, dismissible per-browser */}
          {!bannerDismissed && (
            <div className="relative bg-[#F2F0EB] border border-[#ECEAE4] rounded-xl p-5 mb-6">
              <button
                onClick={dismissBanner}
                aria-label={t('selections.templates.banner.close')}
                title={t('selections.templates.banner.close')}
                className="absolute top-3 right-3 text-[#9E9890] hover:text-[#1C1A17] transition-colors"
              >
                <X size={16} />
              </button>
              <h2 className="text-[15px] font-[800] text-[#1C1A17] mb-2 pr-8">
                {t('selections.templates.banner.title')}
              </h2>
              <p className="text-[12px] font-[600] text-[#1C1A17] leading-relaxed">
                {t('selections.templates.banner.intro')}
              </p>
              <h3 className="text-[13px] font-[800] text-[#1C1A17] mt-3 mb-1">
                {t('selections.templates.banner.howItWorksTitle')}
              </h3>
              <ul className="text-[12px] font-[600] text-[#1C1A17] leading-relaxed space-y-0.5">
                <li>• {t('selections.templates.banner.howItWorks1')}</li>
                <li>• {t('selections.templates.banner.howItWorks2')}</li>
                <li>• {t('selections.templates.banner.howItWorks3')}</li>
              </ul>
              <p className="text-[12px] font-[600] text-[#1C1A17] leading-relaxed mt-3">
                {t('selections.templates.banner.preloadedNote')}
              </p>
            </div>
          )}

          {/* "+ New template" button just above the list */}
          <div className="flex justify-end mb-3">
            <button
              onClick={() => setEditorState({ id: null, draft: EMPTY_DRAFT })}
              className="flex items-center gap-1.5 bg-[#06539B] hover:bg-[#14457E] text-white text-[13px] font-[800] px-4 py-2.5 rounded-full transition-colors"
            >
              <Plus size={14} /> {t('selections.templates.newTemplate')}
            </button>
          </div>

          {/* Empty state */}
          {!isLoading && templates && templates.length === 0 && !editorState && (
            <div className="bg-white border border-dashed border-[#CFD8E3] rounded-2xl p-10 text-center">
              <div className="w-14 h-14 rounded-full bg-[#EEF2FF] flex items-center justify-center mx-auto mb-4">
                <Sparkles size={26} className="text-[#06539B]" />
              </div>
              <h3 className="text-[16px] font-[900] tracking-[-0.3px] text-[#1C1A17]">
                {t('selections.templates.title')}
              </h3>
              <p className="text-[12px] font-[600] text-[#9E9890] mt-1.5 max-w-md mx-auto">
                {t('selections.templates.noTemplates')}
              </p>
            </div>
          )}

          {/* Templates list */}
          <div className="space-y-3">
            {templates?.map(tpl => {
              const segment = PRESET_SEGMENTS[tpl.name]
              // Defensive: a malformed/old-shape template record may lack the
              // `categories` array. Never call .reduce/.length/.map on undefined
              // during render — that throws into the module ErrorBoundary.
              const cats = tpl.categories ?? []
              const totalAllowance = cats.reduce((sum, cat) => sum + (cat.allowance || 0), 0)
              return (
              <div key={tpl.id} className="bg-white border border-[#ECEAE4] rounded-xl p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-[14px] font-[900] text-[#1C1A17] truncate">{localizeTemplateName(tpl.name, isEs)}</h3>
                    {localizeTemplateDescription(tpl.name, tpl.description, isEs) && (
                      <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">{localizeTemplateDescription(tpl.name, tpl.description, isEs)}</p>
                    )}
                    <div className="text-[10px] font-[700] text-[#06539B] mt-1.5">
                      {t('selections.templates.import.categoriesCount', { count: cats.length })}
                    </div>
                    <div className="text-[11px] font-[800] text-[#1C1A17] mt-0.5">
                      {t('selections.templates.card.estimatedTotal', { amount: totalAllowance.toLocaleString() })}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                    {segment && (
                      <>
                        <span className={`text-[9px] font-[800] uppercase tracking-[0.5px] px-2.5 py-1 rounded-full ${SEGMENT_BADGE_STYLE[segment]}`}>
                          {t(`selections.templates.badge.${segment}`)}
                        </span>
                        <span className="text-[9px] font-[800] uppercase tracking-[0.5px] px-2.5 py-1 rounded-full bg-[#F2F0EB] text-[#9E9890]">
                          {t('selections.templates.badge.preloaded')}
                        </span>
                      </>
                    )}
                    <button
                      onClick={() => setEditorState({
                        id: tpl.id,
                        draft: {
                          name: tpl.name,
                          description: tpl.description || '',
                          categories: cats.map(c => ({
                            name: c.name,
                            description: c.description || '',
                            allowance: c.allowance,
                            sortOrder: c.sortOrder,
                          })),
                        },
                      })}
                      className="flex items-center gap-1 text-[11px] font-[800] text-[#06539B] hover:bg-[#EEF2FF] px-3 py-1.5 rounded-full"
                    >
                      <Edit3 size={12} /> {t('selections.options.actions.edit')}
                    </button>
                    <button
                      onClick={() => {
                        const msg = t('selections.templates.confirmDelete', { name: localizeTemplateName(tpl.name, isEs), count: cats.length })
                        if (window.confirm(msg)) deleteTpl.mutate(tpl.id)
                      }}
                      className="flex items-center gap-1 text-[11px] font-[800] text-red-500 hover:bg-red-50 px-3 py-1.5 rounded-full"
                    >
                      <Trash2 size={12} /> {t('selections.options.actions.delete')}
                    </button>
                  </div>
                </div>

                {/* Category rows */}
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {cats.map(c => (
                    <div key={c.id} className="flex items-center justify-between bg-[#FAFAF8] border border-[#F2F0EB] rounded-lg px-3 py-2">
                      <div className="text-[11px] font-[800] text-[#1C1A17] truncate">{localizeCategoryName(c.name, isEs)}</div>
                      <div className="text-[11px] font-[800] text-[#06539B]">${c.allowance.toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>
              )
            })}
          </div>
        </div>

        {/* Editor (nested modal — overlays the library when active) */}
        {editorState && (
          <TemplateEditor
            state={editorState}
            isEs={isEs}
            t={t}
            onClose={() => setEditorState(null)}
            onSave={(draft) => {
              if (editorState.id) updateTpl.mutate({ id: editorState.id, draft })
              else createTpl.mutate(draft)
            }}
            isSaving={createTpl.isPending || updateTpl.isPending}
          />
        )}
      </div>
    </div>
  )
}

// ── EDITOR ────────────────────────────────────────

interface TemplateEditorProps {
  state: { id: string | null; draft: TemplateDraft }
  isEs: boolean
  t: (k: string, opts?: Record<string, unknown>) => string
  onClose: () => void
  onSave: (draft: TemplateDraft) => void
  isSaving: boolean
}

function TemplateEditor({ state, isEs, t, onClose, onSave, isSaving }: TemplateEditorProps) {
  // Defensive: normalise the draft so categories is always an array — the
  // editor maps/reduces over it during render and must never crash on a
  // malformed draft.
  const [draft, setDraft] = useState<TemplateDraft>({ ...state.draft, categories: state.draft.categories ?? [] })

  const isValid = useMemo(() => {
    if (!draft.name.trim()) return false
    if (draft.categories.length === 0) return false
    return draft.categories.every(c => c.name.trim().length > 0 && c.allowance >= 0)
  }, [draft])

  const moveCategory = (idx: number, direction: -1 | 1) => {
    const next = [...draft.categories]
    const target = idx + direction
    if (target < 0 || target >= next.length) return
    const tmp = next[idx]
    next[idx] = next[target]
    next[target] = tmp
    setDraft({ ...draft, categories: next.map((c, i) => ({ ...c, sortOrder: i })) })
  }

  const updateCategory = (idx: number, patch: Partial<CategoryDraft>) => {
    const next = [...draft.categories]
    next[idx] = { ...next[idx], ...patch }
    setDraft({ ...draft, categories: next })
  }

  const addCategory = () => {
    setDraft({
      ...draft,
      categories: [
        ...draft.categories,
        { name: '', description: '', allowance: 0, sortOrder: draft.categories.length },
      ],
    })
  }

  const removeCategory = (idx: number) => {
    const next = draft.categories.filter((_, i) => i !== idx).map((c, i) => ({ ...c, sortOrder: i }))
    setDraft({ ...draft, categories: next })
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#ECEAE4] px-5 py-3 flex items-center justify-between">
          <h3 className="text-[14px] font-[900] text-[#1C1A17]">
            {state.id ? t('selections.options.actions.edit') : t('selections.templates.newTemplate')}
          </h3>
          <button onClick={onClose} className="text-[#9E9890] hover:text-[#1C1A17]">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Top fields */}
          <label className="block">
            <div className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">
              {t('selections.templates.form.name')}<span className="text-red-500 ml-0.5">*</span>
            </div>
            <input
              type="text"
              value={draft.name}
              onChange={e => setDraft({ ...draft, name: e.target.value })}
              className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
            />
          </label>
          <label className="block">
            <div className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">
              {t('selections.templates.form.description')}
            </div>
            <textarea
              value={draft.description}
              onChange={e => setDraft({ ...draft, description: e.target.value })}
              rows={2}
              className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
            />
          </label>

          {/* Categories */}
          <div>
            <div className="text-[11px] font-[800] uppercase tracking-[0.5px] text-[#1C1A17] mb-2">
              {t('selections.templates.form.categories')}
            </div>
            <div className="space-y-2">
              {draft.categories.map((c, idx) => (
                <div key={idx} className="bg-[#FAFAF8] border border-[#F2F0EB] rounded-lg p-3 space-y-2">
                  <div className="flex items-start gap-2">
                    {/* Up/down reorder buttons (no @dnd-kit dependency) */}
                    <div className="flex flex-col gap-0.5 flex-shrink-0">
                      <button
                        onClick={() => moveCategory(idx, -1)}
                        disabled={idx === 0}
                        className="p-1 rounded hover:bg-white disabled:opacity-30"
                        title={isEs ? 'Subir' : 'Up'}
                      >
                        <ArrowUp size={12} />
                      </button>
                      <button
                        onClick={() => moveCategory(idx, 1)}
                        disabled={idx === draft.categories.length - 1}
                        className="p-1 rounded hover:bg-white disabled:opacity-30"
                        title={isEs ? 'Bajar' : 'Down'}
                      >
                        <ArrowDown size={12} />
                      </button>
                    </div>
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <input
                        type="text"
                        placeholder={t('selections.templates.form.categoryName')}
                        value={c.name}
                        onChange={e => updateCategory(idx, { name: e.target.value })}
                        className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B] sm:col-span-2"
                      />
                      <div className="flex items-center gap-1.5">
                        <div className="relative flex-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[12px] font-[700] text-[#9E9890]">$</span>
                          <input
                            type="number"
                            placeholder={t('selections.templates.form.allowance')}
                            value={c.allowance || ''}
                            onChange={e => updateCategory(idx, { allowance: parseFloat(e.target.value) || 0 })}
                            className="w-full border border-[#ECEAE4] rounded-lg pl-7 pr-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
                          />
                        </div>
                        <span
                          className="text-[#9E9890] hover:text-[#06539B] cursor-help flex-shrink-0"
                          title={t('selections.templates.tooltip.allowance')}
                          aria-label={t('selections.templates.tooltip.allowance')}
                        >
                          <HelpCircle size={14} />
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => removeCategory(idx)}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded flex-shrink-0"
                      title={t('selections.options.actions.delete')}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={addCategory}
              className="mt-2 w-full flex items-center justify-center gap-1.5 border border-dashed border-[#CFD8E3] rounded-lg py-2 text-[11px] font-[800] text-[#06539B] hover:bg-[#EEF2FF] transition-colors"
            >
              <Plus size={12} /> {t('selections.templates.form.addCategory')}
            </button>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#ECEAE4] px-5 py-3 flex gap-2 justify-end">
          <button onClick={onClose} className="border border-[#ECEAE4] rounded-full px-4 py-2 text-[12px] font-[800] text-[#9E9890] hover:bg-[#F2F0EB]">
            {t('Cancel')}
          </button>
          <button
            onClick={() => onSave(draft)}
            disabled={!isValid || isSaving}
            className="flex items-center gap-1.5 bg-[#06539B] hover:bg-[#14457E] text-white rounded-full px-5 py-2 text-[12px] font-[800] disabled:opacity-40 transition-colors"
          >
            <Save size={12} /> {isSaving ? '…' : t('Save')}
          </button>
        </div>
      </div>
    </div>
  )
}

function serializeDraft(draft: TemplateDraft) {
  return {
    name: draft.name.trim(),
    description: draft.description.trim() || undefined,
    categories: draft.categories.map((c, idx) => ({
      name: c.name.trim(),
      description: c.description.trim() || undefined,
      allowance: Number(c.allowance) || 0,
      sortOrder: idx,
    })),
  }
}
